import Clock, { ClockCallbackObject } from "./Clock"
import Entity, { CollisionType, Direction } from "./Entity"
import Explosion, { ExplosionType } from "./Explosion"
import Level from "./Level"
import Match from "./Match"
import Player from "./Player"
import { TILE_SIZE, SCALE_FACTOR, SHOW_BOUNDING_BOXES, SHOW_DEBUG_GRAPHICS } from "./config"
import { getAnimation } from "./spritesheet"
import { Rectangle } from "pixi.js"

export default class Bomb extends Entity {
  static BOMB_WIDTH = TILE_SIZE * SCALE_FACTOR
  static BOMB_HEIGHT = 17 * SCALE_FACTOR
  static BOMB_TIMER = 2500

  size: number
  timer: number
  private _owner: Player | null = null
  private _timeout: ClockCallbackObject

  constructor(params: { x: number, y: number, level: Level, size: number, timer?: number, owner?: Player }) {
    super({
      x: params.x,
      y: params.y,
      level: params.level,
      speed: 10,
      width: Bomb.BOMB_WIDTH,
      height: Bomb.BOMB_HEIGHT,
      textures: getAnimation("bomb")
    })
    this.zIndex = 0
    this.animationSpeed = 0.060
    this.setAtTileCoords()
    this.size = params.size
    this.timer = params.timer || Bomb.BOMB_TIMER
    this.play()

    this._timeout = Clock.setTimeout(() => {
      this.stopMoving()
      this.explode()
    }, this.timer)


    this.setBoundingBox(new Rectangle(0, 0, Level.LEVEL_TILE_SIZE, Level.LEVEL_TILE_SIZE))

    if (params.owner) {
      this._owner = params.owner
      this._owner.currentlyPlacedBombs++
    }
  }

  isBlocking(_entityConstructor: typeof Entity.constructor | Entity): boolean {
    if (_entityConstructor instanceof Player && _entityConstructor.recentlyPlacedBomb === this) {
      return false
    }

    return true
  }

  canBeKicked(direction: Direction): boolean {
    if (this.isMoving) {
      return false
    }

    const { x, y } = this.getTileCoords()
    let nextTileX = x
    let nextTileY = y
    const padding = Level.LEVEL_TILE_SIZE / 3
    const threshold = Level.LEVEL_TILE_SIZE
    switch (direction) {
      case "Up":
        nextTileY -= threshold
        break
      case "Down":
        nextTileY += threshold
        break
      case "Left":
        nextTileX -= threshold
        break
      case "Right":
        nextTileX += threshold
        break
    }

    const area = new Rectangle(nextTileX + padding, nextTileY + padding, threshold - padding * 2, threshold - padding * 2)

    if (SHOW_DEBUG_GRAPHICS && SHOW_BOUNDING_BOXES) {
      this.graphics.rect(area.x, area.y, area.width, area.height)
      this.graphics.fill(0xff00ff)
    }
    const entities = this.level.getEntitiesInArea(area, [this])
    return entities.every(entity => !entity.isBlocking(Bomb))
  }

  explode() {
    Clock.clear(this._timeout)
    this.setAtTileCoords()
    super.explode()
    this.removeFromLevel()
    this.level.addChild(new Explosion({ x: this.x, y: this.y, level: this.level, type: "fireMiddleCross" }))
    this._extendExplosion("UP")
    this._extendExplosion("DOWN")
    this._extendExplosion("LEFT")
    this._extendExplosion("RIGHT")
    if (this._owner) {
      this._owner.currentlyPlacedBombs--
    }
  }

  private _addExplosion(params: { x: number, y: number, type: ExplosionType, level: Level }) {
    let placed = false
    this.level.getEntitiesAtTileCoords({ x: params.x, y: params.y }).forEach(entity => {
      if (entity instanceof Explosion) {
        placed = true
        if (
          Explosion.HORIZONTAL_TYPES.includes(entity.type) && Explosion.VERTICAL_TYPES.includes(params.type) ||
          Explosion.VERTICAL_TYPES.includes(entity.type) && Explosion.HORIZONTAL_TYPES.includes(params.type)
        ) {
          entity.type = "fireMiddleCross"
        } else if (entity.type.includes("End") && params.type.includes("Middle")) {
          entity.type = params.type
        }
      }
    })

    if (!placed) {
      this.level.addChild(new Explosion(params))
    }
  }

  private _extendExplosion(direction: "UP" | "DOWN" | "LEFT" | "RIGHT") {
    for (let i = 1; i <= this.size; i++) {
      let nextExplosionCoords
      switch (direction) {
        case "UP":
          nextExplosionCoords = Level.getTileCoords({ x: this.x, y: this.y - i * Explosion.EXPLOSION_SIZE })
          break
        case "DOWN":
          nextExplosionCoords = Level.getTileCoords({ x: this.x, y: this.y + i * Explosion.EXPLOSION_SIZE })
          break
        case "LEFT":
          nextExplosionCoords = Level.getTileCoords({ x: this.x - i * Explosion.EXPLOSION_SIZE, y: this.y })
          break
        case "RIGHT":
          nextExplosionCoords = Level.getTileCoords({ x: this.x + i * Explosion.EXPLOSION_SIZE, y: this.y })
          break
      }

      const entities = this.level.getEntitiesAtTileCoords({ x: nextExplosionCoords.x, y: nextExplosionCoords.y })

      let blocked = false

      entities.forEach(entity => {
        if (entity.isBlocking(Explosion)) {
          blocked = true
          entity.explode()
        }
        // other entities will explode colliding with the explosion
      })
      if (blocked) {
        break
      }

      const finalTile = this.size === i

      switch (direction) {
        case "UP":
          this._addExplosion({ x: nextExplosionCoords.x, y: nextExplosionCoords.y, level: this.level, type: finalTile ? "fireEndTop" : "fireMiddleVertical" })
          break
        case "DOWN":
          this._addExplosion({ x: nextExplosionCoords.x, y: nextExplosionCoords.y, level: this.level, type: finalTile ? "fireEndBottom" : "fireMiddleVertical" })
          break
        case "LEFT":
          this._addExplosion({ x: nextExplosionCoords.x, y: nextExplosionCoords.y, level: this.level, type: finalTile ? "fireEndLeft" : "fireMiddleHorizontal" })
          break
        case "RIGHT":
          this._addExplosion({ x: nextExplosionCoords.x, y: nextExplosionCoords.y, level: this.level, type: finalTile ? "fireEndRight" : "fireMiddleHorizontal" })
          break
      }
    }
  }


  collide(entity: Entity, type: CollisionType): void {
    super.collide(entity, type)
    this.stopMoving()
  }

  stopMoving(): void {
    super.stopMoving()
    this.setAtTileCoords()
  }

  move() {
    if (this.level instanceof Match && !this.level.started) return
    if (!this.alive) return

    const { x, y } = this.getTileCoords()
    let nextTileX = x
    let nextTileY = y
    let nextX = this.x
    let nextY = this.y

    const padding = Level.LEVEL_TILE_SIZE / 3
    const threshold = Level.LEVEL_TILE_SIZE
    switch (this.direction) {
      case "Up":
        nextTileY -= threshold
        nextY -= this.speed
        break
      case "Down":
        nextTileY += threshold
        nextY += this.speed
        break
      case "Left":
        nextTileX -= threshold
        nextX -= this.speed
        break
      case "Right":
        nextTileX += threshold
        nextX += this.speed
        break
    }

    const area = new Rectangle(nextTileX + padding, nextTileY + padding, threshold - padding * 2, threshold - padding * 2)

    if (SHOW_DEBUG_GRAPHICS && SHOW_BOUNDING_BOXES) {
      this.graphics.rect(area.x, area.y, area.width, area.height)
      this.graphics.fill(0xffff10)
    }
    const entities = this.level.getEntitiesInArea(area, [this])

    if (!entities.some(entity => entity.isBlocking(Bomb))) {
      this.x = nextX
      this.y = nextY
    } else {
      this.stopMoving()
      this.setAtTileCoords()
    }
  }

  toString() {
    return "Bomb"
  }
}
